import CronBuilder, { type CronBuilderExpression } from "cron-builder";
import { startCase } from "lodash-es";
import { parseExpression } from "cron-parser";
import { getFormattedDate } from "@SeemplicityDev/shared";
import { DateTime } from "luxon";

const repeatValues = {
  DAY: "DAY",
  WEEK: "WEEK",
  MONTH: "MONTH",
} as const;

type RepeatValue = (typeof repeatValues)[keyof typeof repeatValues];

type Timezone = "local" | "utc";

interface TimeOption {
  value: number;
  label: string;
}

interface AtTime {
  hour: TimeOption | null;
}

export const getRandTime = (): AtTime => {
  const randCronTime = Math.floor(Math.random() * 24);

  return {
    hour: {
      value: randCronTime,
      label: randCronTime.toString().padStart(2, "0"),
    },
  };
};

export const defaultTime = {
  onTime: [] as number[],
  atTime: [getRandTime()],
  repeat: repeatValues.DAY as RepeatValue,
};

const { DAY, WEEK, MONTH } = repeatValues;

const getFormattedTime = (hour: number): string => {
  const dateTime = DateTime.fromFormat(hour.toString(), "H");
  return dateTime.toFormat("HH");
};

const normalizeCronDayOfWeekZeroToSeven = (
  cron: string | null | undefined
): string | null | undefined => {
  if (!cron) {
    return cron;
  }

  const parts = cron.trim().split(/\s+/);
  if (parts.length < 5) {
    return cron.trim();
  }

  const dayOfWeekIndex = parts.length - 1;
  const dayOfWeek = parts[dayOfWeekIndex];
  const normalizedDayOfWeek = dayOfWeek
    .split(",")
    .map((value) => (value === "0" ? "7" : value))
    .join(",");

  parts[dayOfWeekIndex] = normalizedDayOfWeek;

  return parts.join(" ");
};

export const weekDays: Record<string, string> = {
  0: "sun",
  1: "mon",
  2: "tue",
  3: "wed",
  4: "thu",
  5: "fri",
  6: "sat",
  7: "sun",
};

const getDaySuffix = (day: string): string => {
  switch (day) {
    case "1":
      return `${day}st`;

    case "2":
      return `${day}nd`;

    case "3":
      return `${day}rd`;

    default:
      return `${day}th`;
  }
};

const getText = (
  cronExp: CronBuilderExpression,
  cronTime: string
): string | undefined => {
  if (cronExp?.dayOfTheMonth?.[0] !== "*") {
    return `Every Month on ${cronExp?.dayOfTheMonth?.[0]
      .split(",")
      ?.map((day) => getDaySuffix(day))
      ?.join(", ")} at ${cronTime}`;
  } else if (cronExp?.dayOfTheWeek?.[0] !== "*") {
    return `Every Week on ${cronExp?.dayOfTheWeek?.[0]
      ?.split(",")
      ?.map((day) => startCase(weekDays[day]))
      ?.join(", ")} at ${cronTime}`;
  } else if (cronExp?.hour || cronExp?.minute) {
    return `Every Day at ${cronTime}`;
  }
};

const convertCronToDate = (cron: string, withOffset?: boolean): string => {
  return cron
    .split(",")
    ?.map((hour) => {
      let dateTime = DateTime.fromFormat(hour, "H");
      if (withOffset) {
        dateTime = dateTime.plus({ minutes: DateTime.now().offset });
      }
      return dateTime.toFormat("HH:mm");
    })
    ?.join(", ");
};

export const getCronText = (
  cron: string | null | undefined,
  withOffset?: boolean
): string | null | undefined => {
  if (!cron) {
    return null;
  }

  const cronExp = new CronBuilder(
    normalizeCronDayOfWeekZeroToSeven(cron) ?? undefined
  );
  const cronExpData = cronExp.getAll();

  let cronTime = "";
  if (cronExpData?.hour) {
    cronTime = convertCronToDate(cronExpData?.hour[0], withOffset);
  }

  return getText(cronExpData, cronTime);
};

export const getNextSync = (
  cron: string | null | undefined
): string | undefined => {
  if (!cron) {
    return;
  }
  const options = {
    currentDate: DateTime.utc().toFormat("yyyy-MM-dd HH:mm:ss"),
  };

  try {
    const interval = parseExpression(cron, options);
    const nextRunTimestamp = interval?.next()?.toString();
    const nextRunTime = DateTime.fromISO(nextRunTimestamp).plus({
      minutes: DateTime.now().offset,
    });

    return getFormattedDate(nextRunTime.toISO() as string | undefined);
  } catch (e) {
    return "Invalid date";
  }
};

const addOffset = (localHour: string, offset: number): string => {
  const dateTime = DateTime.fromFormat(localHour, "H").plus({
    minutes: offset,
  });
  return dateTime.toFormat("HH");
};

const subtractOffset = (localHour: string, offset: number): string => {
  const dateTime = DateTime.fromFormat(localHour, "H").minus({
    minutes: offset,
  });
  return dateTime.toFormat("HH");
};

interface ConvertCronToTimeResult {
  onTime: number[] | Record<string, never>;
  atTime: AtTime[] | Record<string, never>;
  repeat: RepeatValue | null;
}

export const convertCronToTime = (
  cron: string | null | undefined,
  timezone?: Timezone
): ConvertCronToTimeResult | null => {
  if (!cron) {
    return null;
  }

  const cronExp = new CronBuilder(
    normalizeCronDayOfWeekZeroToSeven(cron) ?? undefined
  );
  const cronExpData = cronExp.getAll();
  const result: ConvertCronToTimeResult = {
    onTime: {},
    atTime: {},
    repeat: null,
  };

  const { hour, dayOfTheMonth, dayOfTheWeek } = cronExpData;

  if (hour?.[0]) {
    const hoursArr = hour?.[0]?.split(",");
    result.atTime = hoursArr.map((cronHour) => {
      let localHour = DateTime.fromFormat(cronHour, "H").toFormat("HH");

      const offset = DateTime.now().offset;
      if (timezone === "local") {
        localHour = addOffset(localHour, offset);
      } else if (timezone === "utc") {
        if (offset > 0) {
          localHour = subtractOffset(localHour, offset);
        } else if (offset < 0) {
          localHour = addOffset(localHour, Math.abs(offset));
        }
      }

      return {
        // `|| null` in the original was dead code (the object literal is always
        // truthy); dropping it preserves runtime behavior and satisfies tsc.
        hour: {
          label: localHour,
          value: parseInt(localHour),
        },
      };
    });
  }

  if (dayOfTheMonth?.[0] !== "*") {
    result.repeat = MONTH;
    result.onTime = dayOfTheMonth?.[0]?.split(",").map((day) => parseInt(day));
  } else if (dayOfTheWeek?.[0] !== "*") {
    result.repeat = WEEK;
    result.onTime = dayOfTheWeek?.[0]?.split(",").map((day) => parseInt(day));
  } else if (hour) {
    result.repeat = DAY;
  }

  return result;
};

export const convertTimeToCron = (
  atTime: AtTime[] | Record<string, never> | undefined,
  onTime: number[] | Record<string, never> | undefined,
  repeat: RepeatValue | null | undefined
): string => {
  let cronExp = new CronBuilder();

  const hours = (atTime as AtTime[])?.map(({ hour }) =>
    getFormattedTime(hour?.value as number)
  );

  cronExp.set("hour", [hours]);
  // minutes are predefined to 00.
  cronExp.set("minute", ["00"]);

  if (repeat === WEEK && (onTime as number[])?.length) {
    cronExp.set(
      "dayOfTheWeek",
      (onTime as number[])?.map((item) => item.toString())
    );
  }
  if (repeat === MONTH && (onTime as number[])?.length) {
    cronExp.set(
      "dayOfTheMonth",
      (onTime as number[])?.map((item) => item.toString())
    );
  }

  return cronExp.build();
};

export const convertCronToUTC = (cron: string | null | undefined): string => {
  const { atTime, repeat, onTime } = convertCronToTime(cron, "utc") ?? {
    atTime: undefined,
    repeat: undefined,
    onTime: undefined,
  };

  return convertTimeToCron(atTime, onTime, repeat);
};

interface TimeInput {
  atTime: AtTime[] | Record<string, never> | undefined;
  onTime: number[] | Record<string, never> | undefined;
  repeat: RepeatValue | null | undefined;
}

export const getTimeText = (
  time: TimeInput | null | undefined
): string | null | undefined => {
  if (!time) {
    return;
  }

  const cron = convertTimeToCron(time.atTime, time.onTime, time.repeat);

  return getCronText(cron);
};
